<script setup lang="ts">
import { computed } from 'vue';
import { Draft } from '@/dashboard/types/draft';
import ContextMenuButton from '@/dashboard/vue/components/common/ContextMenu/ContextMenuButton.vue';
import { showNotification } from '@/dashboard/vue/notifications/notification_actions';
import { useDraftImageStore } from '@/dashboard/store/draftImageStore';

const props = defineProps<{
  draft: Draft;
  zoom: number;
  point: { x: number; y: number };
  downloadUrl: string;
}>();

const draftImageStore = useDraftImageStore();

const drawArea = computed(() => document.querySelector('.drawing-area'));
const drawBounds = computed(() => drawArea.value?.getBoundingClientRect());
const rightMousePoint = computed(() => {
  if (!drawBounds.value) {
    return {
      x: 0,
      y: 0,
    };
  }

  return {
    x: props.point.x > drawBounds.value.width - 200 ? props.point.x - 193 : props.point.x,
    y: props.point.y > drawBounds.value.height - 100 ? props.point.y - 85 : props.point.y,
  };
});

async function createComment() {
  const data = {
    size: {
      width: 0,
      height: 0,
    },
    location: {
      x: Math.round(props.point.x / props.zoom),
      y: Math.round(props.point.y / props.zoom),
    },
    position: 1,
  };
  draftImageStore.createTempPoint(data);
  draftImageStore.setRightMousePoint(null);
}

async function checkClipboardPermissions() {
  try {
    // Check the permission status for clipboard-write
    const result = await navigator.permissions.query({ name: 'clipboard-write' as any });

    if (result.state === 'granted') {
      return true;
    } else return result.state === 'prompt';
  } catch (error) {
    return false;
  }
}

async function copyImageAsPng() {
  try {
    const src = props.draft.image?.original_url ?? '';
    const response = await fetch(src);
    const blob = await response.blob();

    // Create an image element to load the blob
    const img = new Image();
    img.src = URL.createObjectURL(blob);

    img.onload = async () => {
      // Create a canvas to draw the image
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const context = canvas.getContext('2d');
      context?.drawImage(img, 0, 0);

      // Convert the canvas content to a PNG blob
      canvas.toBlob(async (pngBlob) => {
        if (!pngBlob) {
          alert('Failed to load image. Please try again.');
          return;
        }

        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'image/png': pngBlob,
            }),
          ]);
        } catch (clipboardError) {
          console.error('Failed to copy image:', clipboardError);
          alert('Failed to copy image. Please ensure you have granted clipboard permissions.');
        }
      }, 'image/png');
    };

    img.onerror = () => {
      console.error('Failed to load image for conversion.');
      alert('Failed to load image. Please try again.');
    };
  } catch (error) {
    console.error('Error fetching image:', error);
    alert('Failed to fetch image. Please check the image source.');
  }
}

async function copyImage() {
  draftImageStore.setRightMousePoint(null);

  const hasPermission = await checkClipboardPermissions();
  if (!hasPermission) {
    showNotification('any_type', {
      emoji: '❌',
      title: 'Clipboard write permission denied.',
    });
    return;
  }

  await copyImageAsPng();
  showNotification('any_type', {
    emoji: '👌',
    title: 'Image has been successfully copied to clipboard',
  });
}

async function close() {
  draftImageStore.setRightMousePoint(null);
}
</script>

<template>
  <div
    class="shadow-menu-s absolute flex w-[193px] flex-col gap-[2px] overflow-hidden rounded-8 bg-white p-[6px] dark:bg-gray-900"
    :style="{ top: rightMousePoint.y, left: rightMousePoint.x }"
  >
    <ContextMenuButton :need-dark="true" :action="createComment" class="">
      <span class="text-caption-s text-black dark:text-white">Create comment</span>
    </ContextMenuButton>
    <a :href="downloadUrl" target="_blank" @click="close()">
      <ContextMenuButton :need-dark="true" class="w-full">
        <span class="text-caption-s text-black dark:text-white">Download image</span>
      </ContextMenuButton>
    </a>
    <ContextMenuButton :need-dark="true" :action="copyImage">
      <span class="text-caption-s text-black dark:text-white">Copy image</span>
    </ContextMenuButton>
  </div>
</template>
